import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { AssetDto, GalleryDto } from '@nusign/picflow-backend-types';
import { Subject, takeUntil } from 'rxjs';
import { AssetActivityMenuComponent } from '../../../account/menus/asset-activity-menu/asset-activity-menu.component';
import { AssetDownloadButtonComponent } from '../../../gallery/components/asset-download-button/asset-download-button.component';
import { ShareAssetDialogComponent } from '../../../gallery/dialogs/share-asset-dialog/share-asset-dialog.component';
import { InferredContextOpenPipe } from '../../../gallery/pipes/inferred-context-open.pipe';
import { AvatarTypePipe } from '../../../pipes/avatar-type.pipe';
import { ContextMenuService } from '../../../services/context-menu.service';
import { DialogService } from '../../../services/dialog.service';
import { GalleryService } from '../../../services/gallery.service';
import { GuestService } from '../../../services/guest.service';
import { StateService } from '../../../services/state.service';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ClickTrackDirective } from '../../../shared/directives/click-track.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { ViewProfileMenuComponent } from '../../../view-gallery/components/view-profile-menu/view-profile-menu.component';
import { GuestProfileDialogComponent } from '../../../view-gallery/dialogs/guest-profile-dialog/guest-profile-dialog.component';
import { canDownloadAsset } from '../../lib/can-download-asset';
import { AssetContextMenuComponent } from '../asset-context-menu/asset-context-menu.component';
import { AssetPageMetaComponent } from '../asset-page-meta/asset-page-meta.component';

@Component({
  selector: 'app-asset-header',
  templateUrl: './asset-header.component.html',
  styleUrls: ['./asset-header.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeaderComponent,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    AssetPageMetaComponent,
    ContextMenuComponent,
    TranslatePipe,
    ViewProfileMenuComponent,
    AsyncPipe,
    AvatarComponent,
    AssetDownloadButtonComponent,
    ClickTrackDirective,
    AvatarTypePipe,
    AssetContextMenuComponent,
    AssetActivityMenuComponent,
    InferredContextOpenPipe,
  ],
})
export class AssetHeaderComponent implements OnInit, OnChanges, OnDestroy {
  private dialog = inject(DialogService);
  public state = inject(StateService);
  private galleryService = inject(GalleryService);
  private guestService = inject(GuestService);
  private check = inject(ChangeDetectorRef);
  public contextService = inject(ContextMenuService);
  @ViewChild('anonymousGuestInfo') anonymousGuestInfoEl: ContextMenuComponent;
  @Input() asset: AssetDto;
  @Input() sideMenuOpen: boolean;
  @Input() gallery: GalleryDto;
  @Input() clientMode: boolean;
  galleryPermissions = input.required<GalleryDto['permissions']>();
  @Input() activeIndex: number;
  @Input() thumbnailBarOpen: boolean;
  @Input() previewWidth: number;
  @Output() onClose = new EventEmitter();
  @Output() onShowInfo = new EventEmitter();
  @Output() onThumbnailBarOpen = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @Output() onUpload = new EventEmitter();
  destroyed$ = new Subject<void>();

  allowDownload = signal(false);

  ngOnInit(): void {
    this.guestService.guest$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.check.detectChanges();
    });
    this.galleryService.onGalleryUpdated$.pipe(takeUntil(this.destroyed$)).subscribe(gallery => {
      if (!gallery || !this.gallery) return;
      if (gallery.id !== this.gallery.id) return;
      this.allowDownload.set(this.canDownload());
    });
  }

  ngOnChanges(): void {
    this.allowDownload.set(this.canDownload());
  }

  close() {
    this.onClose.emit('');
  }

  private canDownload() {
    return canDownloadAsset(this.asset, this.gallery, this.galleryPermissions());
  }

  openShareDialog() {
    this.dialog.open(ShareAssetDialogComponent, {
      gallery: this.gallery,
      asset: this.asset,
    });
  }

  openGuestProfileDialog() {
    this.dialog.open(GuestProfileDialogComponent, { gallery: this.gallery });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
